import React, { useRef, useEffect } from 'react';
import { useWebRTCContext } from '../WebRTCContext';
import * as S from '../styled';

// Debug helper for transcript changes
const DEBUG = process.env.NODE_ENV !== 'production';

const ConversationDisplay = () => {
  const { state } = useWebRTCContext();
  const { transcript, connectionState } = state;
  const transcriptRef = useRef();
  
  // Auto-scroll to bottom when transcript updates
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
    
    // Debug log transcript updates when they happen
    if (DEBUG && transcript.length > 0) {
      const lastItem = transcript[transcript.length - 1];
      console.log('%c[TRANSCRIPT] Updated:', 'color: purple; font-weight: bold', 
        `Items: ${transcript.length}`, 
        `Last: [${lastItem.cmd}] "${lastItem.text.substring(0, 30)}${lastItem.text.length > 30 ? '...' : ''}"`
      );
    }
  }, [transcript]);
  
  // If not connected and no transcript, display a message
  if (transcript.length === 0 && connectionState !== 'connecting') {
    return (
      <S.TranscriptContainer ref={transcriptRef}>
        <S.Transcript>
          <S.Line type="log">
            {connectionState === 'connected' 
              ? 'Waiting for conversation to begin...' 
              : connectionState === 'disconnected' 
              ? 'Call ended. Press "Try Again" to start a new conversation.' 
              : 'Conversation transcript will appear here...'}
          </S.Line>
        </S.Transcript>
      </S.TranscriptContainer>
    );
  }
  
  // If connecting, show connecting message
  if (connectionState === 'connecting' && transcript.length === 0) {
    return (
      <S.TranscriptContainer ref={transcriptRef}>
        <S.Transcript>
          <S.Line type="log">
            Establishing connection to the AI assistant...
          </S.Line>
        </S.Transcript>
      </S.TranscriptContainer>
    );
  }
  
  // If connected but no transcript yet, show waiting message
  if (connectionState === 'connected' && transcript.length === 0) {
    return (
      <S.TranscriptContainer ref={transcriptRef}>
        <S.Transcript>
          <S.Line type="log">
            Connected! Waiting for transcription data...
          </S.Line>
          {DEBUG && (
            <S.Line type="log">
              <small style={{opacity: 0.7}}>
                (Debug: Checking if WebSocket is properly connected with the correct session ID)
              </small>
            </S.Line>
          )}
        </S.Transcript>
      </S.TranscriptContainer>
    );
  }
  
  // Format message content for display
  const formatMessage = (text, type) => {
    // System messages don't need special formatting
    if (['log', 'error'].includes(type)) {
      return <>{text}</>;
    }
    
    // For AI and human messages, add the label inline
    return (
      <>
        <S.Root type={type}>
          {['ai', 'ai-log'].includes(type) ? 'AI:' : 'You:'}
        </S.Root>
        {text}
      </>
    );
  };
  
  return (
    <S.TranscriptContainer ref={transcriptRef}>
      <S.Transcript>
        {transcript.map((item, index) => {
          const { cmd, text } = item;
          return (
            <S.Line type={cmd} key={index}>
              {formatMessage(text, cmd)}
            </S.Line>
          );
        })}
      </S.Transcript>
    </S.TranscriptContainer>
  );
};

export default ConversationDisplay; 